import {useState, useCallback, useMemo} from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const parseEmails = (text) =>
    text.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean);

const useEmailInput = (existingEmails = []) => {
    const [emails, setEmails] = useState([]);
    const [inputValue, setInputValue] = useState('');

    const existingSet = useMemo(
        () => new Set(existingEmails.map((e) => e.toLowerCase())),
        [existingEmails],
    );

    const addEmails = useCallback((raw) => {
        const parsed = parseEmails(raw);
        if (!parsed.length) return;

        setEmails((prev) => {
            const currentSet = new Set(prev.map((e) => e.address));
            const newEmails = [];
            for (const address of parsed) {
                if (currentSet.has(address)) continue;
                currentSet.add(address);
                newEmails.push({
                    address,
                    isInvalid: !EMAIL_REGEX.test(address),
                    isDuplicate: existingSet.has(address),
                });
            }
            return [...prev, ...newEmails];
        });
    }, [existingSet]);

    const removeEmail = useCallback((index) => {
        setEmails((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
            e.preventDefault();
            if (inputValue.trim()) {
                addEmails(inputValue);
                setInputValue('');
            }
        }
        if (e.key === 'Backspace' && !inputValue) {
            setEmails((prev) => prev.slice(0, -1));
        }
    }, [inputValue, addEmails]);

    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        addEmails(pasted);
        setInputValue('');
    }, [addEmails]);

    const handleBlur = useCallback(() => {
        if (inputValue.trim()) {
            addEmails(inputValue);
            setInputValue('');
        }
    }, [inputValue, addEmails]);

    const reset = useCallback(() => {
        setEmails([]);
        setInputValue('');
    }, []);

    const markDuplicates = useCallback((duplicateAddresses) => {
        const dupeSet = new Set(duplicateAddresses.map((e) => e.toLowerCase()));
        setEmails((prev) => prev.map((e) =>
            dupeSet.has(e.address) ? {...e, isDuplicate: true} : e,
        ));
    }, []);

    const invalidCount = emails.filter((e) => e.isInvalid).length;
    const duplicateCount = emails.filter((e) => e.isDuplicate).length;
    const validEmails = emails.filter((e) => !e.isInvalid && !e.isDuplicate);

    return {
        emails,
        inputValue,
        setInputValue,
        addEmails,
        removeEmail,
        handleKeyDown,
        handlePaste,
        handleBlur,
        reset,
        markDuplicates,
        invalidCount,
        duplicateCount,
        validEmails,
    };
};

export default useEmailInput;
